import React, { useState } from 'react';

import {
  ACCOUNT_BUDGETS,
  ACCOUNT_DURATIONS,
  ACCOUNT_PARTY_SIZES,
  ACCOUNT_SOCIAL_TYPES,
  accountParagraphs,
  accountYears,
} from './accountModel';
import { useAccountRecord } from './AccountRecordContext';
import { AccountPageShell } from './AccountPageShell';
import { AccountDialog } from './AccountOverlays';
import { AccountIdentityAvatar } from './AccountIdentity';
import {
  ACCOUNT_PREFERRED_SUPPLIERS,
  ACCOUNT_PROFILER_NEEDS_ATTENTION,
  ACCOUNT_PROFILER_URL,
  ACCOUNT_SHOWCASE_URL,
  ACCOUNT_SITE_DOMAIN,
  type AccountRecord,
} from './accountFixture';
import {
  AccountCard,
  BandHead,
  CardColumn,
  Chip,
  ExternalLink,
  FieldPair,
  Grid,
  Hint,
  ImagePlate,
  Muted,
  NavLink,
  Row,
  RowList,
  ScopeChip,
  SelectField,
  SmallButton,
  StatusChip,
  TagField,
  TextAreaField,
  TextField,
} from './primitives';

/**
 * AR-380 — Brand + Public Presence.
 *
 * v15 folded the marketing half of Business + Brand into Public Presence
 * (D-ACCT-30, D-ACCT-32): one page for everything the public reads — the brand,
 * the advisor bio, the specialties and the public contact. Together they ARE
 * the WorldVia Advisor Showcase, so the Showcase card is gone: the page IS
 * where it is managed, and the header carries its state and its doors.
 *
 * What remains at the foot is the one listing managed outside this app, the
 * Travel Leaders Agent Profiler. The website card left with Sites + Marketing.
 *
 * The record cards save as a DRAFT; Publish changes is what makes the profile
 * live. Three Showcase fields do not render in Phase 1 (D-ACCT-45).
 */

/** Typical duration, typical travellers and the booking CTA wait for Phase 2. */
const SHOWCASE_DEFERRED = true;

/** The profile card a prospective client sees. One component, two call sites. */
const ProfilePreview: React.FC<{
  pub: AccountRecord['pub'];
  cta: AccountRecord['cta'];
}> = ({ pub, cta }) => (
  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3.5 dark:border-gray-700 dark:bg-gray-900/40">
    <div className="flex items-center gap-3">
      <AccountIdentityAvatar />
      <span>
        <b className="block text-base text-gray-900 dark:text-gray-100">
          {pub.first} {pub.last}
        </b>
        <small className="block text-[13px] text-gray-500 dark:text-gray-400">
          {pub.title}
        </small>
        <small className="block text-[13px] text-gray-500 dark:text-gray-400">
          {cta.city}, {cta.state}
        </small>
      </span>
    </div>
    <p className="mt-2 text-[14.5px] leading-relaxed text-gray-700 dark:text-gray-300">
      {pub.headline}
    </p>
    <span className="mt-2.5 inline-block rounded-md bg-orange-700 px-3 py-1.5 text-sm font-medium text-white">
      {cta.cta}
    </span>
  </div>
);

export const AccountPublic: React.FC = () => {
  const { record, isOpen, draftValue, publish, setDraftValue } = useAccountRecord();
  // The owner can force one brand on the whole team; then it is not this
  // member's setting to change.
  const brandForced = record.teamset.brand !== "Each advisor's own brand";
  const p = record.pub;
  const s = record.spec;
  const c = record.cta;

  const [bioOpen, setBioOpen] = useState(false);
  const [preview, setPreview] = useState(false);
  const [share, setShare] = useState<null | 'showcase' | 'site'>(null);
  const [copied, setCopied] = useState(false);
  const [, repaintSocial] = useState(0);
  // Held in memory: there is no file store yet, and a blob URL in the
  // persisted record would be a broken image after a reload.
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  const bioParas = accountParagraphs(p.bio);
  const pubOpen = isOpen('pub');
  const previewSource = pubOpen
    ? ({
        ...p,
        first: String(draftValue('pub.first') ?? p.first),
        last: String(draftValue('pub.last') ?? p.last),
        title: String(draftValue('pub.title') ?? p.title),
        headline: String(draftValue('pub.headline') ?? p.headline),
      } as AccountRecord['pub'])
    : p;

  const socialDraft = (draftValue('pub.social') as AccountRecord['pub']['social']) ?? [];

  const shareUrl =
    share === 'showcase'
      ? ACCOUNT_SHOWCASE_URL
      : record.web.mode === 'sites'
        ? ACCOUNT_SITE_DOMAIN
        : record.web.url || ACCOUNT_SITE_DOMAIN;
  const shareLine =
    share === 'showcase'
      ? `Take a look at my advisor profile: ${shareUrl}`
      : `Take a look at my website: ${shareUrl}`;

  // The logo and the headshot are changed by clicking them; the Replace logo
  // button and its tooltip are gone (D-ACCT-43).
  const LogoPlate: React.FC = () => (
    <ImagePlate
      what="your logo"
      onPick={setLogoUrl}
      className="flex h-16 w-24 items-center justify-center rounded-lg border border-gray-200 bg-white px-3 dark:border-gray-700"
    >
      {logoUrl ? (
        <img src={logoUrl} alt={record.biz.brand} className="max-h-full max-w-full object-contain" />
      ) : (
        <span className="text-xs text-gray-600 dark:text-gray-400">{record.biz.brand}</span>
      )}
    </ImagePlate>
  );

  const HeadshotPlate: React.FC = () => (
    <ImagePlate what="your headshot" onPick={setPhotoUrl} className="rounded-full">
      {photoUrl ? (
        <img src={photoUrl} alt="Your headshot" className="h-24 w-24 rounded-full object-cover" />
      ) : (
        <AccountIdentityAvatar size="hero" />
      )}
    </ImagePlate>
  );

  const headerActions = (
    <>
      <span className="mr-1 inline-flex items-center gap-1.5 text-[13.5px] text-gray-500 dark:text-gray-400">
        <StatusChip status={record.pubmeta.dirty ? 'Draft' : 'Published'} />
        Last published {record.pubmeta.published}
      </span>
      <SmallButton onClick={() => setPreview(true)}>Preview</SmallButton>
      <SmallButton onClick={() => setShare('showcase')}>Share</SmallButton>
    </>
  );

  return (
    <AccountPageShell
      page="public"
      onPreview={() => setPreview(true)}
      headerActions={headerActions}
    >
      <CardColumn wide={pubOpen}>
        {/* The brand the public reads — it came here from Business + Brand. */}
        <AccountCard
          cardKey="brand"
          name="Brand"
          chip={brandForced ? <ScopeChip scope="owner" /> : undefined}
          form={
            <>
              <LogoPlate />
              <TextField path="biz.brand" label="Brand name" />
              <TextField path="biz.tagline" label="Tagline" />
              <TextAreaField path="biz.desc" label="Business description" />
            </>
          }
        >
          <Row label="Logo">
            <div className="mt-1.5 flex items-center gap-3.5">
              <LogoPlate />
            </div>
            <Hint>PNG, JPG or SVG</Hint>
          </Row>
          <Row label="Brand name" value={record.biz.brand} />
          <Row label="Tagline" value={record.biz.tagline} />
          <Row
            label="Business description"
            value={record.biz.desc}
            chip={record.biz.desc ? undefined : <StatusChip status="Incomplete" />}
            last
          />
        </AccountCard>

        <AccountCard
          cardKey="pub"
          name="Advisor bio"
          chip={<ScopeChip scope="public" />}
          side={<ProfilePreview pub={previewSource} cta={c} />}
          form={
            <>
              <div>
                <span className="mb-1 block text-[13px] font-semibold uppercase tracking-[0.1em] text-gray-500 dark:text-gray-400">
                  Headshot
                </span>
                <HeadshotPlate />
                <Hint>PNG or JPG</Hint>
              </div>
              <FieldPair>
                <TextField path="pub.first" label="Public first name" />
                <TextField path="pub.last" label="Public last name" />
              </FieldPair>
              <TextAreaField
                path="pub.title"
                label="Headline"
                hint="Shown beside your name. "
                cap={80}
                rows={2}
              />
              <TextAreaField
                path="pub.headline"
                label="Short introduction"
                hint="Used on profile cards and in search results. "
                cap={200}
                rows={3}
              />
              <TextAreaField
                path="pub.bio"
                label="Full bio"
                hint="Shown on your full advisor profile. One paragraph per block, blank line between them."
                rows={8}
              />
              <SelectField
                path="pub.since"
                label="In travel since"
                options={accountYears(1990, new Date().getFullYear())}
              />
              <TagField
                path="pub.awards"
                label="Awards + certifications"
                placeholder="Add an award or certification"
              />
            </>
          }
        >
          {/* Clicking the headshot changes it — the Replace button is gone
              (D-ACCT-43). */}
          <Row label="Headshot">
            <div className="mt-1.5 flex items-center gap-3.5">
              <HeadshotPlate />
            </div>
            <Hint>PNG or JPG</Hint>
          </Row>
          <Row label="Public name" value={`${p.first} ${p.last}`} />
          <Row label="Headline" value={p.title} />
          <Row label="Short introduction" value={p.headline} />
          <Row label="Full bio">
            <div className={bioOpen ? '' : 'line-clamp-2'}>
              {bioParas.map((t, i) => (
                <p
                  key={i}
                  className="mt-0.5 text-[14.5px] leading-relaxed text-gray-700 dark:text-gray-300"
                >
                  {t}
                </p>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setBioOpen((v) => !v)}
              className="mt-1 inline-flex min-h-[24px] items-center py-1 text-[13.5px] text-orange-700 hover:underline dark:text-orange-400"
            >
              {bioOpen ? 'Show less' : 'Show more'}
            </button>
          </Row>
          <Row label="In travel since" value={p.since} />
          <RowList label="Awards + certifications" items={p.awards} last />
        </AccountCard>

        <AccountCard
          cardKey="spec"
          name="Specialties + expertise"
          chip={<ScopeChip scope="public" />}
          form={
            <>
              <TagField
                path="spec.dest"
                label="Destinations"
                placeholder="Start typing a destination"
              />
              <TagField
                path="spec.interests"
                label="Client interests"
                placeholder="Start typing a client interest"
              />
              <TagField
                path="spec.styles"
                label="Travel styles"
                placeholder="Start typing a travel style"
              />
              <SelectField
                path="spec.budget"
                label="Typical budget range"
                options={ACCOUNT_BUDGETS}
              />
            </>
          }
        >
          <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
            <RowList label="Destinations" items={s.dest} />
            <RowList label="Client interests" items={s.interests} />
            <RowList label="Travel styles" items={s.styles} />
            {/* The preferred suppliers are the Suppliers page's list, read off
                it — one record, two surfaces. */}
            <RowList
              label="Preferred suppliers"
              items={ACCOUNT_PREFERRED_SUPPLIERS}
              extra={<NavLink to="/suppliers">your list →</NavLink>}
            />
            <Row label="Typical budget range" value={s.budget} />
            <Row label="Typical duration" value={s.duration} />
            <Row label="Typical number of travelers" value={s.party} last />
          </div>
        </AccountCard>

        <AccountCard
          cardKey="cta"
          name="Public contact + booking"
          chip={<ScopeChip scope="public" />}
          form={
            <>
              <TextField path="cta.email" label="Public email" />
              <TextField path="cta.phone" label="Public phone" />
              <FieldPair>
                <TextField path="cta.city" label="City" />
                <TextField path="cta.state" label="State" />
              </FieldPair>
              <div>
                <span className="mb-1 block text-[13px] font-semibold uppercase tracking-[0.1em] text-gray-500 dark:text-gray-400">
                  Social links
                </span>
                {socialDraft.map((link, i) => (
                  /* The fields are uncontrolled, so the key has to change when a
                     row is spliced out — otherwise the rows below keep the DOM
                     nodes, and their old values, while the draft has shifted. */
                  <div
                    key={`${socialDraft.length}-${i}-${link.ty}-${link.h}`}
                    className="mt-2.5 flex items-end gap-2.5"
                  >
                    <div className="min-w-0 flex-1">
                      <FieldPair>
                        <SelectField
                          path={`pub.social.${i}.ty`}
                          label="Type"
                          options={ACCOUNT_SOCIAL_TYPES}
                        />
                        <TextField
                          path={`pub.social.${i}.h`}
                          label="Handle or address"
                        />
                      </FieldPair>
                    </div>
                    {socialDraft.length > 1 ? (
                      <SmallButton
                        onClick={() => {
                          socialDraft.splice(i, 1);
                          repaintSocial((n) => n + 1);
                        }}
                      >
                        Remove
                      </SmallButton>
                    ) : null}
                  </div>
                ))}
                <div className="mt-3">
                  <SmallButton
                    onClick={() => {
                      setDraftValue('pub.social', [
                        ...socialDraft,
                        { ty: 'Instagram', h: '' },
                      ]);
                      repaintSocial((n) => n + 1);
                    }}
                  >
                    Add another link
                  </SmallButton>
                </div>
              </div>
            </>
          }
        >
          <Row label="Public email" value={c.email} />
          <Row label="Public phone" value={c.phone} />
          <Row label="Public location" value={`${c.city}, ${c.state}`} />
          <Row label="Social links" last>
            <div className="mt-1.5 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {p.social
                .filter((x) => x.h)
                .map((x, i) => (
                  <span key={i} className="min-w-0">
                    <b className="block text-[13.5px] text-gray-900 dark:text-gray-100">
                      {x.ty}
                    </b>
                    <small className="block truncate text-[13px] text-gray-500 dark:text-gray-400">
                      {x.h}
                    </small>
                  </span>
                ))}
            </div>
          </Row>
        </AccountCard>
      </CardColumn>

      {/* The one listing managed outside this app. The directory does not tell
          us when it last changed, so the card does not claim to know. */}
      <div className="mt-5 max-w-[720px] rounded-lg border border-gray-200 bg-white px-4 py-3.5 dark:border-gray-700 dark:bg-[#1A2021]">
        <div className="mb-3 flex items-baseline justify-between gap-3">
          <span className="text-[13px] font-semibold uppercase tracking-[0.14em] text-orange-700 dark:text-orange-400">
            Travel Leaders Agent Profiler
          </span>
          <StatusChip
            status={ACCOUNT_PROFILER_NEEDS_ATTENTION ? 'Action required' : 'Published'}
          />
        </div>
        <p className="-mt-1 mb-2 text-[14px] text-gray-500 dark:text-gray-400">
          Your Agent Profiler listing is managed in Travel Leaders&apos; own app, not here.
        </p>
        <Row
          label="Profile URL"
          value={<span className="break-all">{ACCOUNT_PROFILER_URL}</span>}
          last
        />
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          <ExternalLink href={ACCOUNT_PROFILER_URL}>View profile ↗</ExternalLink>
          <ExternalLink href={ACCOUNT_PROFILER_URL}>Manage profile ↗</ExternalLink>
        </div>
      </div>

      {preview ? (
        <AccountDialog
          title="Your public profile"
          sub={record.pubmeta.dirty ? 'As it will publish' : 'As it is published'}
          onClose={() => setPreview(false)}
        >
          <div className="mt-3">
            <ProfilePreview pub={p} cta={c} />
          </div>
        </AccountDialog>
      ) : null}

      {share ? (
        <AccountDialog
          title={share === 'showcase' ? 'Share your profile' : 'Share your website'}
          onClose={() => {
            setShare(null);
            setCopied(false);
          }}
          actions={
            <>
              <SmallButton
                primary
                onClick={() => {
                  void navigator.clipboard?.writeText(shareUrl);
                  setCopied(true);
                }}
              >
                {copied ? 'Copied' : 'Copy link'}
              </SmallButton>
              <a
                className="rounded-md border border-gray-200 bg-white px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                href={`mailto:?subject=${encodeURIComponent(
                  share === 'showcase' ? 'My advisor profile' : 'My website',
                )}&body=${encodeURIComponent(shareLine)}`}
              >
                Email it
              </a>
            </>
          }
        >
          <div className="mt-3">
            <label
              className="mb-1 block text-[13px] font-semibold uppercase tracking-[0.1em] text-gray-500 dark:text-gray-400"
              htmlFor="account-share-url"
            >
              Address
            </label>
            <input
              id="account-share-url"
              type="text"
              readOnly
              value={shareUrl}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-[15px] text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
            />
            <p className="mt-2.5 text-[14.5px] text-gray-700 dark:text-gray-300">
              {shareLine}
            </p>
          </div>
        </AccountDialog>
      ) : null}
    </AccountPageShell>
  );
};

export default AccountPublic;
